import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import {
  fetchTicketByIdThunk,
  taskAcceptThunk,
  taskCompleteThunk,
  updateTicketByIdThunk,
} from "./currentTicketOperations";
import type { Ticket } from "../../ticketSharedTypes";
import { Ticket as ApiTicket } from "@/api/hausify";
import type { RootState } from "@/store/store";
import {
  REQUEST_STATUS,
  type RequestStatus,
} from "@/modules/constants/REQUEST_STATUS";

interface CurrentTicketState extends Partial<Ticket> {
  status: RequestStatus;
  error: string | null;
  ticketManagerTaskIsLoading: boolean;
}

const initialState: CurrentTicketState = {
  status: REQUEST_STATUS.IDLE,
  error: null,
  ticketManagerTaskIsLoading: false,
};

const slice = createSlice({
  name: "currentTicket",
  initialState,
  reducers: {
    resetCurrentTicket: (): CurrentTicketState => ({
      status: REQUEST_STATUS.IDLE,
      error: null,
      ticketManagerTaskIsLoading: false,
    }),
  },
  extraReducers: (builder) => {
    // fetch ticket
    builder.addCase(fetchTicketByIdThunk.pending, (state) => {
      state.status = REQUEST_STATUS.LOADING;
      state.error = null;
    });
    builder.addCase(
      fetchTicketByIdThunk.fulfilled,
      (state, action: PayloadAction<ApiTicket>) => {
        Object.assign(state, action.payload);
        state.status = REQUEST_STATUS.SUCCEEDED;
        state.error = null;
      },
    );
    builder.addCase(fetchTicketByIdThunk.rejected, (state, action) => {
      state.status = REQUEST_STATUS.FAILED;
      state.error = action.error?.message || null;
    });

    // update ticket
    builder.addCase(updateTicketByIdThunk.pending, (state) => {
      state.status = REQUEST_STATUS.LOADING;
      state.error = null;
    });
    builder.addCase(updateTicketByIdThunk.rejected, (state, action) => {
      state.status = REQUEST_STATUS.FAILED;
      state.error = action.error?.message || null;
    });
    builder.addCase(
      updateTicketByIdThunk.fulfilled,
      (state, action: PayloadAction<Ticket | undefined>) => {
        if (action.payload) {
          Object.assign(state, action.payload);
        }
        state.status = REQUEST_STATUS.SUCCEEDED;
        state.error = null;
      },
    );

    // specific ticket-manager operations
    // accept ticket
    builder.addCase(taskAcceptThunk.pending, (state) => {
      state.ticketManagerTaskIsLoading = true;
    });
    builder.addCase(taskAcceptThunk.fulfilled, (state) => {
      state.ticketManagerTaskIsLoading = false;
    });

    // complete ticket
    builder.addCase(taskCompleteThunk.pending, (state) => {
      state.ticketManagerTaskIsLoading = true;
    });
    builder.addCase(taskCompleteThunk.fulfilled, (state) => {
      state.ticketManagerTaskIsLoading = false;
    });
  },
});

const selectSelf = (state: RootState): CurrentTicketState =>
  (state.tickets.ticketDetail as any).currentTicket;

const selectCurrentTicket = (state: RootState): CurrentTicketState =>
  selectSelf(state);

const selectCurrentTicketStatus = (state: RootState): RequestStatus =>
  selectSelf(state).status;

const selectCurrentTicketError = (state: RootState): string | null =>
  selectSelf(state).error;

export const currentTicketSelectors = {
  selectCurrentTicket,
  selectCurrentTicketStatus,
  selectCurrentTicketError,
};

// Export the inferred ticket type for components to use
export type CurrentTicketType = ReturnType<typeof selectCurrentTicket>;

export const { resetCurrentTicket } = slice.actions;

export const reducer = slice.reducer;
